<script lang="ts">
  import { onMount, afterUpdate } from "svelte";
  import Swiper from "swiper";
  import { Navigation, Pagination } from "swiper/modules";
  import type { SwiperOptions } from "swiper/types";
  import type { ButtonStyle } from "./Swiper/ContentSwiper.svelte";
  
  export let uniqueId: string;
  export let showSeeAllButton = false;
  export let showNavButtons = true;
  export let seeAllButtonText: string | undefined = undefined;
  export let seeAllUrl: string = "#";
  export let buttonStyle: ButtonStyle = "primary";
  export let letterSpacing: string = "1.5px";
  export let buttonPaddingVertical: string = "12px";
  export let buttonPaddingHorizontal: string = "18px";
  
  // Navigation button styling props
  export let navButtonBorderColor: string = "";
  export let navButtonBorderStyle: string = "";
  export let navButtonBorderThickness: string = "";
  export let navButtonBorderRadius: string = "";
  export let navButtonChevronColor: string = "";
  export let navButtonBackgroundColor: string = "";
  export let navButtonWidth: string = "";
  export let navButtonHeight: string = "";
  export let navButtonHoverBorderColor: string = "";
  export let navButtonHoverBackgroundColor: string = "";
  
  // Pagination styling props
  export let paginationBulletWidth: string = "";
  export let paginationBulletHeight: string = "";
  export let paginationActiveBulletWidth: string = "";
  export let paginationActiveBulletHeight: string = "";
  export let paginationBulletBorderRadius: string = "";
  export let paginationInactiveBulletColor: string = "";
  export let paginationActiveBulletColor: string = "";
    
  let swiperEl: HTMLElement | null = null;
  let swiperInstance: Swiper;
  let isInitialized = false;
  let buttonVisible = false;
  let isVisible = false;
  
  // Force update button visibility whenever props change
  $: {
    buttonVisible = showSeeAllButton;
  }
  
  // Use afterUpdate to ensure button visibility is set correctly after component updates
  afterUpdate(() => {
    // Force button visibility if checkbox is checked
    if (showSeeAllButton && !buttonVisible) {
      buttonVisible = true;
    }
  });
  
  // Configurable Swiper options with defaults
  export let swiperOptions: SwiperOptions = {
    modules: [Navigation, Pagination],
    slidesPerView: 'auto',
    spaceBetween: 24,
    navigation: {
      enabled: false
    },
    pagination: {
      enabled: true,
      clickable: true,
      dynamicBullets: true,
      dynamicMainBullets: 3
    },
    watchOverflow: true,
    observer: true,
    observeParents: true,
    resizeObserver: true,
    updateOnWindowResize: true
  };

  const handlePrevClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (swiperInstance) {
      swiperInstance.slidePrev();
    }
  };

  const handleNextClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (swiperInstance) {
      swiperInstance.slideNext();
    }
  };

  // Build CSS variable style strings
  $: navButtonStyles = `
    ${navButtonBorderColor ? `--nav-button-border-color: ${navButtonBorderColor};` : ''}
    ${navButtonBorderStyle ? `--nav-button-border-style: ${navButtonBorderStyle};` : ''}
    ${navButtonBorderThickness ? `--nav-button-border-thickness: ${navButtonBorderThickness};` : ''}
    ${navButtonBorderRadius ? `--nav-button-border-radius: ${navButtonBorderRadius};` : ''}
    ${navButtonChevronColor ? `--nav-button-chevron-color: ${navButtonChevronColor};` : ''}
    ${navButtonBackgroundColor ? `--nav-button-background-color: ${navButtonBackgroundColor};` : ''}
    ${navButtonWidth ? `--nav-button-width: ${navButtonWidth};` : ''}
    ${navButtonHeight ? `--nav-button-height: ${navButtonHeight};` : ''}
    ${navButtonHoverBorderColor ? `--nav-button-hover-border-color: ${navButtonHoverBorderColor};` : ''}
    ${navButtonHoverBackgroundColor ? `--nav-button-hover-background-color: ${navButtonHoverBackgroundColor};` : ''}
  `;
  
  $: paginationStyles = `
    ${paginationBulletWidth ? `--pagination-bullet-width: ${paginationBulletWidth};` : ''}
    ${paginationBulletHeight ? `--pagination-bullet-height: ${paginationBulletHeight};` : ''}
    ${paginationActiveBulletWidth ? `--pagination-active-bullet-width: ${paginationActiveBulletWidth};` : ''}
    ${paginationActiveBulletHeight ? `--pagination-active-bullet-height: ${paginationActiveBulletHeight};` : ''}
    ${paginationBulletBorderRadius ? `--pagination-bullet-border-radius: ${paginationBulletBorderRadius};` : ''}
    ${paginationInactiveBulletColor ? `--pagination-inactive-bullet-color: ${paginationInactiveBulletColor};` : ''}
    ${paginationActiveBulletColor ? `--pagination-active-bullet-color: ${paginationActiveBulletColor};` : ''}
  `;

  onMount(() => {
    if (!swiperEl) return;
    
    const config: SwiperOptions = {
      ...swiperOptions,
      slidesPerView: 1,
      spaceBetween: 16,
      breakpoints: {
        0: {
          slidesPerView: 1,
          spaceBetween: 16,
        },
        480: {
          slidesPerView: 2,
          spaceBetween: 20,
        },
        768: {
          slidesPerView: 3,
          spaceBetween: 24,
        },
        992: {
          slidesPerView: 3,
          spaceBetween: 24,
        },
        1200: {
          slidesPerView: 5,
          spaceBetween: 24,
        }
      },
      pagination: {
        enabled: true,
        clickable: true,
        el: `.${uniqueId}-pagination`,
        bulletClass: 'swiper-pagination-bullet',
        bulletActiveClass: 'swiper-pagination-bullet-active',
        modifierClass: 'swiper-pagination-',
        dynamicBullets: false
      },
      on: {
        afterInit: () => {
          isInitialized = true;
          setTimeout(() => {
            updateNavigationVisibility();
            isVisible = true;
          }, 100);
        },
        slideChange: () => {
          updateNavigationVisibility();
        },
        resize: () => {
          if (swiperInstance) {
            swiperInstance.update();
          }
          updateNavigationVisibility();
        },
        reachEnd: () => {
          updateNavigationVisibility();
        },
        reachBeginning: () => {
          updateNavigationVisibility();
        },
        fromEdge: () => {
          updateNavigationVisibility();
        }
      }
    };

    // Initialize swiper with a small delay to ensure DOM is ready
    setTimeout(() => {
      if (swiperEl) {
        swiperInstance = new Swiper(swiperEl, config);
        isInitialized = true;
        updateNavigationVisibility();
      }
    }, 50);

    const prevButton = (swiperEl as HTMLElement).querySelector(`.${uniqueId}-prev`);
    const nextButton = (swiperEl as HTMLElement).querySelector(`.${uniqueId}-next`);
    
    if (prevButton) {
      const newPrevButton = prevButton.cloneNode(true);
      prevButton.parentNode?.replaceChild(newPrevButton, prevButton);
    }
    
    if (nextButton) {
      const newNextButton = nextButton.cloneNode(true);
      nextButton.parentNode?.replaceChild(newNextButton, nextButton);
    }

    const handleResize = () => {
      if (swiperInstance) {
        swiperInstance.update();
        updateNavigationVisibility();
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (swiperInstance) {
        swiperInstance.destroy();
      }
    };
  });

  const updateNavigationVisibility = () => {
    if (!swiperInstance || !isInitialized) return;

    const navButtons = document.querySelector(`.${uniqueId}-container .nav-buttons`);
    const prevButton = document.querySelector(`.${uniqueId}-container .${uniqueId}-prev`);
    const nextButton = document.querySelector(`.${uniqueId}-container .${uniqueId}-next`);
    
    if (navButtons) {
      (navButtons as HTMLElement).style.display = 'flex';
    }

    const isAtEnd = swiperInstance.isEnd || 
      (swiperInstance.activeIndex >= swiperInstance.slides.length - (typeof swiperInstance.params?.slidesPerView === 'number' ? swiperInstance.params.slidesPerView : 1));

    if (prevButton) {
      (prevButton as HTMLElement).classList.toggle('swiper-button-disabled', swiperInstance.isBeginning);
    }

    if (nextButton) {
      (nextButton as HTMLElement).classList.toggle('swiper-button-disabled', isAtEnd);
    }
  };

  $: buttonStyles = `
    letter-spacing: ${letterSpacing};
    padding: ${buttonPaddingVertical} ${buttonPaddingHorizontal};
  `;
  
  // Separate functions to handle hover effects
  let isPrevButtonHovered = false;
  let isNextButtonHovered = false;
  
  const handlePrevButtonMouseEnter = () => {
    isPrevButtonHovered = true;
  };
  
  const handlePrevButtonMouseLeave = () => {
    isPrevButtonHovered = false;
  };
  
  const handleNextButtonMouseEnter = () => {
    isNextButtonHovered = true;
  };
  
  const handleNextButtonMouseLeave = () => {
    isNextButtonHovered = false;
  };
</script>

<svelte:head>
<!-- Default Swiper modules styles in Shadow DOM -->
<!-- Import Swiper CSS modules only once as they are always present in the Shadow DOM -->
  <style>
    @import 'swiper/css/navigation';
    @import 'swiper/css/pagination';
    
    /* Global swiper pagination styling */
    .swiper-pagination {
      position: static !important;
      width: auto !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }
    
    .swiper-pagination-bullet {
      width: var(--pagination-bullet-width, 8px) !important;
      height: var(--pagination-bullet-height, 8px) !important;
      margin: 0 4px !important;
      border-radius: var(--pagination-bullet-border-radius, 50%) !important;
      background-color: var(--pagination-inactive-bullet-color, #CCCCCC) !important;
      opacity: 1 !important;
      cursor: pointer !important;
    }

    .swiper-pagination-bullet-active {
      width: var(--pagination-active-bullet-width, 16px) !important;
      height: var(--pagination-active-bullet-height, 8px) !important;
      background-color: var(--pagination-active-bullet-color, #000000) !important;
    }
  </style>
</svelte:head>

<style lang="scss">
/* ************************************************** */
/* These styles are used to style the default swiper and must exist in the Shadow DOM at all times */
/* ************************************************** */
  :global(.swiper) {
    width: 100%;
    overflow: visible;
  }

  :global(.swiper-wrapper) {
    display: flex;
    width: 100%;
  }

  :global(.swiper-pagination) {
    position: static;
    width: auto;
    display: flex;
    align-items: center;
  }
  /* ************************************************** */
  /* Make sure to use the :global() inside a uniquely named class */
  /* ************************************************** */
  .card-swiper-container {
    position: relative;
    margin: 0;
    overflow: hidden;
    padding: 2rem 0;
    width: 100%;
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.3s ease, visibility 0.3s ease;
    
    &.is-visible {
      visibility: visible;
      opacity: 1;
    }

    .card-swiper-wrapper {
      :global(.swiper-slide) {
        height: auto;
        transition: opacity 0.3s ease;
      }
      
      /* Ensure card contents maintain aspect ratio */
      :global(.event-card),
      :global(.offer-card),
      :global(.store-card) {
        height: 100%;
        display: flex;
        flex-direction: column;
        
        :global(.event-image),
        :global(.offer-image),
        :global(.store-image),
        :global(.store-logo-container) {
          aspect-ratio: 1;
          height: 0;
          padding-top: 100%; /* Creates a perfect square */
          position: relative;
          width: 100%;
          overflow: hidden;
          
          :global(img) {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }
      }
    }

    .card-swiper-pagination {
      margin: 0 auto;
      text-align: center;
    }
  }

  .card-swiper-footer {
    display: flex;
    align-items: center;
    margin-top: 2rem;
    position: relative;
    flex-wrap: wrap;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 24px;
    }

    .swiper-controls {
      display: flex;
      align-items: center;
      gap: 20px;

      @media (max-width: 768px) {
        width: 100%;
        flex-direction: column;
        align-items: center;
        gap: 12px;
      }

      .nav-buttons {
        display: flex;
        gap: 20px;

        @media (max-width: 768px) {
          width: 100%;
          justify-content: space-between;
        }

        .nav-button {
          flex-shrink: 0;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          
          /* Navigation button styles using CSS variables */
          width: var(--nav-button-width, 40px);
          height: var(--nav-button-height, 40px);
          border-width: var(--nav-button-border-thickness, 1px);
          border-style: var(--nav-button-border-style, solid);
          border-color: var(--nav-button-border-color, #DDDDDD);
          border-radius: var(--nav-button-border-radius, 0px);
          background-color: var(--nav-button-background-color, #FFFFFF);
          color: var(--nav-button-chevron-color, #000000);
          
          &:hover {
            border-color: var(--nav-button-hover-border-color, #BBBBBB);
            background-color: var(--nav-button-hover-background-color, #F5F5F5);
          }

          svg {
            width: 24px;
            height: 24px;
            flex-shrink: 0;
          }

          &:global(.swiper-button-disabled) {
            opacity: 0.5;
            pointer-events: none;
          }
        }
      }
    }
    .see-all-button-wrapper {
      margin-left: auto;
      
      @media (max-width: 768px) {
        margin-left: 0;
        width: 100%;

        .btn {
          width: 100%;
        }
      }
    }
  }

  .hidden {
    display: none !important;
  }
</style>

<div class="card-swiper-container {uniqueId}-container" class:is-visible={isVisible} style="{navButtonStyles} {paginationStyles}">
  <div class="swiper card-swiper" bind:this={swiperEl}>
    <div class="swiper-wrapper card-swiper-wrapper">
      <slot />
    </div>
  </div>

  <div class="swiper-footer card-swiper-footer">
    <div class="swiper-controls" class:hidden={!showNavButtons}>
      <div class="nav-buttons" class:hidden={!showNavButtons}>
        <button 
          class="nav-button prev {uniqueId}-prev" 
          on:click={handlePrevClick} 
          on:mouseenter={handlePrevButtonMouseEnter}
          on:mouseleave={handlePrevButtonMouseLeave}
          style={isPrevButtonHovered ? `border-color: var(--nav-button-hover-border-color, #BBBBBB); background-color: var(--nav-button-hover-background-color, #F5F5F5);` : ''}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <button 
          class="nav-button next {uniqueId}-next" 
          on:click={handleNextClick} 
          on:mouseenter={handleNextButtonMouseEnter}
          on:mouseleave={handleNextButtonMouseLeave}
          style={isNextButtonHovered ? `border-color: var(--nav-button-hover-border-color, #BBBBBB); background-color: var(--nav-button-hover-background-color, #F5F5F5);` : ''}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
      <div class="swiper-pagination card-swiper-pagination {uniqueId}-pagination" 
        style="--pagination-bullet-width: {paginationBulletWidth}; --pagination-bullet-height: {paginationBulletHeight}; --pagination-active-bullet-width: {paginationActiveBulletWidth}; --pagination-active-bullet-height: {paginationActiveBulletHeight}; --pagination-bullet-border-radius: {paginationBulletBorderRadius}; --pagination-inactive-bullet-color: {paginationInactiveBulletColor}; --pagination-active-bullet-color: {paginationActiveBulletColor};">
      </div>
    </div>
    
    {#if buttonVisible}
      <div class="see-all-button-wrapper">
        <a 
          href={seeAllUrl} 
          class="btn btn-{buttonStyle}" 
          style={buttonStyles}
        >
          {seeAllButtonText || "See all"}
        </a>
      </div>
    {/if}
  </div>
</div> 